/**
 * The table pace setting, and the one override the player does not set.
 *
 * Small on purpose: this is the whole of the "settings module" Phase 3 needs. Phase 2b's
 * Settings sheet will grow a UI around it; nothing here should have to change when it
 * does, because the sheet will read and write through `getPace` / `setPace` like
 * everything else.
 *
 * `prefers-reduced-motion` is read from the browser and never stored: it is the
 * operating system's setting, and a player who turns it on there expects every page to
 * honour it without being asked again. It forces Fast with fades only (D11).
 *
 * Every browser touch is guarded, so the Director's tests can import this under Node.
 */
import { durationsFor, type Durations, type Pace } from "./pace";

const KEY = "bb.table.pace";
const PACES: readonly Pace[] = ["fast", "normal", "cinematic"];

let current: Pace | null = null;
const listeners = new Set<() => void>();

function read(): Pace {
  try {
    const v = globalThis.localStorage?.getItem(KEY);
    if (v && (PACES as readonly string[]).includes(v)) return v as Pace;
  } catch { /* private mode, or no storage: the default is fine */ }
  return "normal";
}

export function getPace(): Pace {
  if (current === null) current = read();
  return current;
}

export function setPace(p: Pace): void {
  current = p;
  try { globalThis.localStorage?.setItem(KEY, p); } catch { /* best effort */ }
  for (const l of listeners) l();
}

/** True when the operating system asks for reduced motion. False under Node. */
export function prefersReducedMotion(): boolean {
  try {
    return globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  } catch { return false; }
}

/** The durations the timeline should use right now. */
export function currentDurations(): Durations {
  return durationsFor(getPace(), prefersReducedMotion());
}

/** Notified when the pace changes. Returns the unsubscribe. */
export function onPaceChange(l: () => void): () => void {
  listeners.add(l);
  return () => { listeners.delete(l); };
}

export const PACE_OPTIONS: { id: Pace; label: string }[] = [
  { id: "fast", label: "Fast" },
  { id: "normal", label: "Normal" },
  { id: "cinematic", label: "Cinematic" },
];
